import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { featuredVideoIds } from "@/mocks/videos";
import { formatViews, getVideoById, typeLabels } from "@/utils/video";

const slides = featuredVideoIds
  .map((id) => getVideoById(id))
  .filter((item): item is Video => Boolean(item));

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const goNext = () => setIndex((prev) => (prev + 1) % slides.length);
  const goPrev = () => setIndex((prev) => (prev - 1 + slides.length) % slides.length);

  useEffect(() => {
    if (paused || slides.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused]);

  if (slides.length === 0) return null;

  const current = slides[index];

  return (
    <section
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
        setPaused(true);
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 48) {
          if (delta < 0) goNext();
          else goPrev();
        }
        touchStartX.current = null;
        setPaused(false);
      }}
      aria-label="焦点视频推荐"
    >
      <div className="relative h-[520px] w-full md:h-[580px] lg:h-[640px]">
        {slides.map((slide, slideIndex) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              slideIndex === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            aria-hidden={slideIndex !== index}
          >
            <img
              src={slide.cover}
              alt={`${slide.title}焦点海报`}
              title={`${slide.title} - ${typeLabels[slide.type]}在线观看`}
              className={`h-full w-full object-cover object-top ${
                slideIndex === index ? "animate-slow-zoom" : ""
              }`}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/25" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-background-50/50" />
          </div>
        ))}

        <div className="page-shell relative z-10 flex h-full w-full flex-col justify-center">
          <div className="max-w-2xl animate-fade-up pb-8 md:pb-0" key={current.id}>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 font-label text-[11px] font-medium text-background-50">
                <i className="ri-fire-line text-xs" />
                焦点推荐
              </span>
              <span className="rounded-full border border-background-300/80 bg-background-100/60 px-3 py-1 font-label text-[11px] text-foreground-700 backdrop-blur">
                {typeLabels[current.type]} · {current.genres.join(" / ")}
              </span>
              <span className="rounded-full bg-accent-500/15 px-3 py-1 font-label text-[11px] text-accent-300">
                {current.year} 新作
              </span>
            </div>

            <h1 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-foreground-950 sm:text-4xl md:text-5xl lg:text-[52px]">
              {current.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-600 md:text-sm">
              <span className="flex items-center gap-1 text-primary-400">
                <i className="ri-star-fill text-sm" />
                <strong className="font-label font-semibold">{current.rating.toFixed(1)}</strong>
              </span>
              <span>{current.duration}</span>
              <span>{current.region}</span>
              <span>导演 {current.director}</span>
              <span className="flex items-center gap-1">
                <i className="ri-play-circle-line text-sm" />
                {formatViews(current.views)}次播放
              </span>
            </div>

            <p className="mt-4 line-clamp-3 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-[15px]">
              {current.summary}
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to={`/detail/${current.id}?play=1`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill text-lg" />
                立即观看
              </Link>
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100/50 px-6 py-3 text-sm text-foreground-900 backdrop-blur transition-colors hover:border-primary-400 hover:text-primary-400"
              >
                查看详情
                <i className="ri-arrow-right-line text-base" />
              </Link>
            </div>
          </div>
        </div>

        <div className="page-shell pointer-events-none absolute inset-x-0 bottom-7 z-20 flex w-full items-end justify-between">
          <div className="pointer-events-auto flex items-center gap-2">
            {slides.map((slide, slideIndex) => (
              <button
                key={slide.id}
                type="button"
                aria-label={`切换到${slide.title}`}
                aria-current={slideIndex === index}
                onClick={() => setIndex(slideIndex)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  slideIndex === index
                    ? "w-9 bg-primary-500"
                    : "w-4 bg-background-500/60 hover:bg-background-400"
                }`}
              />
            ))}
            <span className="ml-1 font-label text-[11px] text-foreground-500">
              {String(index + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
            </span>
          </div>

          <div className="pointer-events-auto hidden items-center gap-2 md:flex">
            <button
              type="button"
              aria-label="上一个焦点视频"
              onClick={() => setIndex((prev) => (prev - 1 + slides.length) % slides.length)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/80 bg-background-100/60 text-foreground-800 backdrop-blur transition-colors hover:border-primary-400 hover:text-primary-400"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              type="button"
              aria-label="下一个焦点视频"
              onClick={() => setIndex((prev) => (prev + 1) % slides.length)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/80 bg-background-100/60 text-foreground-800 backdrop-blur transition-colors hover:border-primary-400 hover:text-primary-400"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}